<template>
  <div
    id="wrapper"
    class="w-100"
  >
    <SidebarMenu
      v-if="!route.meta.hideSidebar"
      :disabled="isEmpty"
      :no-serial="getRegionalizedFeatures.noSerialForm"
    />

    <div
      id="content"
      :class="{'with-sidebar': !route.meta.hideSidebar}"
    >
      <main class="d-flex flex-column flex-grow-1 flex-shrink-0 position-relative">
        <router-view v-slot="{ Component }">
          <transition name="fade">
            <component
              :is="Component"
              :sn="getCurrentSerial"
              :disabled="isEmpty"
              :authorized="isCurrentMachineAuthorized($userContext)"
            />
          </transition>
        </router-view>
      </main>
      <BFooter
        :ota-logo-url="appConfig.absolutePath + appConfig.myMachinePath + '/img/ota-logo.png'"
        :disclaimer="translatedLabel('disclaimer', 'shared_Footer')"
        :privacy-policy="getRegion === 'EMEA' ? translatedLabel('privacyPolicyTitle', 'shared_Footer') : ''"
        :privacy-policy-content="translatedLabel('privacyPolicyContent', 'shared_Footer')"
        :languages="languages"
        :selected-language="getLanguage"
        @language-picked="saveLanguage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, watch, getCurrentInstance} from 'vue'
import {useStore} from 'vuex'
import {useRoute} from 'vue-router'
import SidebarMenu from './SidebarMenu.vue'
import BFooter from '@dxp-common-components-updated/footer'
import {useCommon} from '@/composables/useCommon'
import appConfig from '@/config'

const {labels, translatedLabel} = useCommon()
const store = useStore()
const route = useRoute()
const instance = getCurrentInstance()
const $userContext = instance?.appContext.config.globalProperties.$userContext

const props = defineProps<{
  user: Record<string, any>
}>()

const isEmpty = computed<boolean>(() => store.getters['mymachine/isEmpty'])
const getCurrentSerial = computed<string>(() => store.getters['mymachine/getCurrentSerial'])
const getLanguage = computed<string>(() => store.getters['mymachine/getLanguage'])
const getAvailableLanguages = computed(() => store.getters['mymachine/getAvailableLanguages'])
const isCurrentMachineAuthorized = computed(() => store.getters['mymachine/isCurrentMachineAuthorized'])
const getRegionalizedFeatures = computed(() => store.getters['mymachine/getRegionalizedFeatures'])
const getRegion = computed<string>(() => store.getters['mymachine/getRegion'])

const languages = computed(() => {
  const ret: { value: string; text: string }[] = []
  const languageNames = labels.value['shared_LanguagesISO'] || {}
  let languageList = getAvailableLanguages.value
  if (languageList) {
    languageList = [...languageList].sort()
    if (languageList.indexOf('en') >= 0) {
      languageList = languageList.filter((item: string) => item !== 'en')
      languageList.unshift('en')
    }
    for (const lang of languageList) {
      if (languageNames && languageNames[lang]) ret.push({value: lang, text: languageNames[lang]})
    }
  }
  return ret
})

watch(() => props.user, (n) => {
  if (Object.keys(n).length !== 0) store.commit('mymachine/saveUser', n)
})

watch(getCurrentSerial, (n, o) => {
  if (n && n !== o) store.dispatch('mymachine/fetchMachine', n)
})

function saveLanguage(lang: string): void {
  store.commit('mymachine/saveLanguage', lang)
}

// created lifecycle
if (getCurrentSerial.value) store.dispatch('mymachine/fetchMachine', getCurrentSerial.value)
</script>

<style lang="scss">
@import '@/scss/grid';

#wrapper {
  padding-top: 4rem;
}

#content {
  main {
    & > section {
      padding: 1rem 1rem;
    }
  }
}

footer {
  z-index: 101;
}

@include media-breakpoint-up('lg') {
  #wrapper {
    flex-grow: 1;
    display: flex;
    padding-top: 4rem;
  }

  #content {
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;

    &.with-sidebar {
      max-width: calc(100vw - 342px);
    }

    main {
      & > section {
        padding: 2rem 1.5rem;
      }
    }
  }
}

@include media-breakpoint-up('xl') {
  #content {
    main {
      & > section {
        padding: 2rem 3rem;
      }
    }
  }
}
</style>
